import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage, getLocale } from '../../contexts/LanguageContext';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import api from '../../api';
import './PepitesPage.css';

/* ── helpers ── */
const fmtDate = (d) => new Date(d).toLocaleDateString(getLocale(), { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
const fmtNum = (n) => (n || 0).toLocaleString(getLocale()).replace(/ /g, ' ');

const PepitesPage = () => {
    const { serverId } = useParams();
    const { success, error: showError } = useNotification();
    const { t } = useLanguage();

    // Config state
    const [channels, setChannels]         = useState([]);
    const [selectedChannel, setSelectedChannel] = useState('');
    const [config, setConfig]             = useState(null);
    const [savingConfig, setSavingConfig] = useState(false);

    // Data state
    const [summary, setSummary]   = useState([]);
    const [entries, setEntries]   = useState([]);
    const [archives, setArchives] = useState([]);
    const [loading, setLoading]   = useState(false);
    const [archiving, setArchiving] = useState(false);
    const [expanded, setExpanded] = useState({});   // playerId -> bool
    const [search, setSearch]     = useState('');

    // Mode paiement : part redistribuée et joueurs déjà payés en jeu (suivi local)
    const [payoutRate, setPayoutRate] = useState(0.8);
    const [payMode, setPayMode] = useState(false);
    const [paidIds, setPaidIds] = useState(() => new Set());

    // Edit state
    const [editingId, setEditingId]     = useState(null);
    const [editAmount, setEditAmount]   = useState('');

    // ── Load channels + config ──────────────────────────────────────────
    useEffect(() => {
        if (!serverId) return;
        // Channels
        api.get(`/discord/channels/${serverId}`)
            .then(r => setChannels((r.data || []).filter(c => c.type === 0).sort((a, b) => a.name.localeCompare(b.name))))
            .catch(() =>
                api.get(`/servers/${serverId}`)
                    .then(r => setChannels((r.data?.salons || []).filter(c => c.type === 0)))
                    .catch(() => setChannels([]))
            );
        // Config
        api.get(`/pepites-config/${serverId}`)
            .then(r => {
                setConfig(r.data);
                setSelectedChannel(r.data?.channelId || '');
                if (typeof r.data?.payoutRate === 'number') setPayoutRate(r.data.payoutRate);
            })
            .catch(() => {});
    }, [serverId]);

    // ── Load data ───────────────────────────────────────────────────────
    const fetchData = useCallback(async () => {
        if (!serverId) return;
        setLoading(true);
        try {
            const [sumRes, entRes, archRes] = await Promise.all([
                api.get(`/pepites/${serverId}/summary`),
                api.get(`/pepites/${serverId}`),
                api.get(`/pepites/${serverId}/archives`)
            ]);
            setSummary(Array.isArray(sumRes.data) ? sumRes.data : []);
            setEntries(Array.isArray(entRes.data) ? entRes.data : []);
            setArchives(Array.isArray(archRes.data) ? archRes.data : []);
        } catch (err) {
            console.error('Erreur chargement pépites:', err);
            showError(t('pepitesAdmin.loadError', 'Erreur lors du chargement des pépites'));
        } finally {
            setLoading(false);
        }
    }, [serverId, showError, t]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ── Save config ─────────────────────────────────────────────────────
    const saveConfig = async () => {
        if (!selectedChannel) return;
        setSavingConfig(true);
        try {
            const ch = channels.find(c => c.id === selectedChannel);
            await api.post(`/pepites-config/${serverId}`, {
                channelId: selectedChannel,
                channelName: ch?.name || selectedChannel
            });
            setConfig({ channelId: selectedChannel, channelName: ch?.name || selectedChannel });
            success(t('pepitesAdmin.channelSaved', 'Salon pépites configuré !'));
        } catch {
            showError(t('pepitesAdmin.saveError', 'Erreur lors de la sauvegarde'));
        } finally {
            setSavingConfig(false);
        }
    };

    // ── Delete entry ────────────────────────────────────────────────────
    const deleteEntry = async (entryId) => {
        if (!window.confirm(t('pepitesAdmin.confirmDelete', 'Supprimer cette entrée ? Cette action est irréversible.'))) return;
        try {
            await api.delete(`/pepites/${serverId}/entries/${entryId}`);
            success(t('pepitesAdmin.entryDeleted', 'Entrée supprimée'));
            fetchData();
        } catch {
            showError(t('pepitesAdmin.deleteError', 'Erreur lors de la suppression'));
        }
    };

    // ── Edit entry ──────────────────────────────────────────────────────
    const saveEdit = async (entryId) => {
        const parsed = parseInt(editAmount, 10);
        if (isNaN(parsed) || parsed <= 0) { showError(t('pepitesAdmin.invalidAmount', 'Montant invalide')); return; }
        try {
            await api.patch(`/pepites/${serverId}/entries/${entryId}`, { amount: parsed });
            success(t('pepitesAdmin.entryUpdated', 'Entrée modifiée'));
            setEditingId(null);
            fetchData();
        } catch {
            showError(t('pepitesAdmin.updateError', 'Erreur lors de la modification'));
        }
    };

    // ── Mode paiement puis archive + remise à zéro ───────────────────
    const openPayMode = () => {
        if (!summary.length) {
            showError(t('pepitesAdmin.nothingToDistribute', 'Aucune pépite à distribuer pour le moment'));
            return;
        }
        setPaidIds(new Set());
        setPayMode(true);
    };

    const togglePaid = (playerId) => setPaidIds(prev => {
        const next = new Set(prev);
        if (next.has(playerId)) next.delete(playerId); else next.add(playerId);
        return next;
    });

    const copyAmount = async (amount) => {
        try {
            await navigator.clipboard.writeText(String(amount));
            success(t('pepitesAdmin.copied', '{n} copié', { n: fmtNum(amount) }));
        } catch { /* presse-papiers indisponible */ }
    };

    const finishPayment = async () => {
        const remaining = summary.length - paidIds.size;
        const confirmation = window.confirm(
            (remaining > 0 ? t('pepitesAdmin.unpaidWarning', '{n} joueur(s) ne sont pas cochés comme payés.', { n: remaining }) + '\n\n' : '') +
            t('pepitesAdmin.confirmFinish', 'Terminer la distribution ? Les compteurs seront remis à zéro et une archive sera conservée.')
        );
        if (!confirmation) return;

        setArchiving(true);
        try {
            await api.post(`/pepites/${serverId}/archive`);
            success(t('pepitesAdmin.finished', 'Distribution terminée, compteurs remis à zéro'));
            setPayMode(false);
            setExpanded({});
            setSearch('');
            await fetchData();
        } catch (err) {
            const apiError = err?.response?.data?.error;
            showError(apiError || t('pepitesAdmin.archiveError', 'Erreur lors de l’archivage des pépites'));
        } finally {
            setArchiving(false);
        }
    };

    // ── Filtered summary ────────────────────────────────────────────────
    const filteredSummary = summary.filter(p =>
        p.playerName.toLowerCase().includes(search.toLowerCase())
    );

    const grandTotal = summary.reduce((acc, p) => acc + p.total, 0);
    const payoutOf = (p) => p.payout ?? Math.round(p.total * payoutRate);
    const totalPayout = summary.reduce((acc, p) => acc + payoutOf(p), 0);
    const ratePct = `${Math.round(payoutRate * 100)} %`;
    const paidAmount = summary.filter(p => paidIds.has(p._id)).reduce((acc, p) => acc + payoutOf(p), 0);

    // ── Entries for a given player ──────────────────────────────────────
    const entriesFor = (playerId) => entries.filter(e => e.playerId === playerId);

    return (
        <div className="c2-scope c2-admin-page">
            <PageHeader
                icon="gem"
                title={t('navigation.pepites', 'Pépites')}
                subtitle={t('pepitesAdmin.subtitle', 'Gestion des pépites de l\'alliance')}
                actions={
                    <>
                        <button type="button" className="c2-btn c2-btn--ghost" onClick={fetchData} disabled={loading}>
                            <Icon name="refresh" size={18} />
                            {t('common.refresh', 'Actualiser')}
                        </button>
                        <button
                            type="button"
                            className="c2-btn c2-btn--accent"
                            onClick={openPayMode}
                            disabled={!summary.length || archiving}
                            title={t('pepitesAdmin.payTitle', 'Affiche les montants à donner en jeu, puis archive et remet les compteurs à zéro')}
                        >
                            <Icon name="gem" size={18} />
                            {t('pepitesAdmin.pay', 'Payer & redistribuer')}
                        </button>
                    </>
                }
            />

            {/* ── Chiffres clés ── */}
            {!loading && summary.length > 0 && (
                <div className="c2-kpis">
                    <div className="c2-kpi">
                        <span className="c2-kpi__value pepites-gold">{fmtNum(grandTotal)}</span>
                        <span className="c2-kpi__label">{t('pepitesAdmin.recycled', 'Pépites recyclées')}</span>
                    </div>
                    <div className="c2-kpi">
                        <span className="c2-kpi__value pepites-payout">{fmtNum(totalPayout)}</span>
                        <span className="c2-kpi__label">{t('pepitesAdmin.toRedistribute', 'À redistribuer ({rate})', { rate: ratePct })}</span>
                    </div>
                    <div className="c2-kpi">
                        <span className="c2-kpi__value">{summary.length}</span>
                        <span className="c2-kpi__label">{t('pepitesAdmin.players', 'Joueurs')}</span>
                    </div>
                    <div className="c2-kpi">
                        <span className="c2-kpi__value">{entries.length}</span>
                        <span className="c2-kpi__label">{t('pepitesAdmin.entries', 'Entrées')}</span>
                    </div>
                </div>
            )}

            {/* ── Configuration du salon ── */}
            <section className="c2-card c2-section">
                <div className="c2-section__head">
                    <h2 className="c2-section__title"><Icon name="hash" size={20} />{t('pepitesAdmin.channelTitle', 'Salon des pépites')}</h2>
                    {config?.channelId && (
                        <span className="c2-status"><Icon name="check" size={16} />{t('pepitesAdmin.currentChannel', 'Salon actuel :')} #{config.channelName || config.channelId}</span>
                    )}
                </div>
                <p className="c2-section__desc">
                    {t('pepitesAdmin.channelDesc', 'Lorsqu\'un joueur poste dans ce salon un message contenant un nombre et un screen, le bot enregistre automatiquement ses pépites.')}
                </p>
                <div className="c2-row">
                    <label className="c2-field pepites-channel">
                        <span className="c2-field__label">{t('pepitesAdmin.discordChannel', 'Salon Discord')}</span>
                        <select
                            className="c2-select"
                            value={selectedChannel}
                            onChange={e => setSelectedChannel(e.target.value)}
                        >
                            <option value="">{t('pepitesAdmin.selectChannel', '-- Sélectionner un salon --')}</option>
                            {channels.map(c => (
                                <option key={c.id} value={c.id}>#{c.name}</option>
                            ))}
                        </select>
                    </label>
                    <button
                        type="button"
                        className="c2-btn c2-btn--primary"
                        onClick={saveConfig}
                        disabled={!selectedChannel || savingConfig}
                    >
                        <Icon name="save" size={18} />
                        {savingConfig ? t('pepitesAdmin.saving', 'Sauvegarde...') : t('common.save', 'Sauvegarder')}
                    </button>
                </div>
            </section>

            {/* ── Récapitulatif par joueur ── */}
            <section className="c2-card c2-section">
                <div className="c2-section__head">
                    <h2 className="c2-section__title"><Icon name="trophy" size={20} />{t('pepitesAdmin.perPlayer', 'Récapitulatif par joueur')}</h2>
                    <input
                        className="c2-input pepites-search"
                        type="search"
                        placeholder={t('pepitesAdmin.searchPlayer', 'Rechercher un joueur...')}
                        aria-label={t('pepitesAdmin.searchPlayerLabel', 'Rechercher un joueur')}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>

                {loading ? (
                    <div className="c2-loading">
                        <div className="c2-spinner" />
                        <p>{t('common.loading', 'Chargement...')}</p>
                    </div>
                ) : filteredSummary.length === 0 ? (
                    <div className="c2-empty">
                        <Icon name="gem" size={36} />
                        <p>{search ? t('pepitesAdmin.noPlayerMatch', 'Aucun joueur ne correspond à la recherche') : t('pepitesAdmin.noneYet', 'Aucune pépite enregistrée pour l\'instant')}</p>
                        {!config?.channelId && (
                            <p className="c2-field__hint">{t('pepitesAdmin.configureFirst', 'Configurez d\'abord un salon Discord ci-dessus.')}</p>
                        )}
                    </div>
                ) : (
                    <div className="c2-table-wrap">
                        <table className="c2-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{t('pepitesAdmin.player', 'Joueur')}</th>
                                    <th className="is-num">{t('pepitesAdmin.totalPepites', 'Total pépites')}</th>
                                    <th className="is-num">{t('pepitesAdmin.toGive', 'À donner ({rate})', { rate: ratePct })}</th>
                                    <th className="is-center">{t('pepitesAdmin.entries', 'Entrées')}</th>
                                    <th>{t('pepitesAdmin.lastEntry', 'Dernière entrée')}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSummary.map((p, i) => {
                                    const isOpen = !!expanded[p._id];
                                    const playerEntries = entriesFor(p._id);
                                    return (
                                        <React.Fragment key={p._id}>
                                            <tr>
                                                <td className="c2-muted">{i + 1}</td>
                                                <td><strong>{p.playerName}</strong></td>
                                                <td className="is-num">
                                                    <span className="c2-chip c2-chip--purple"><Icon name="gem" size={14} />{fmtNum(p.total)}</span>
                                                </td>
                                                <td className="is-num"><strong className="pepites-payout">{fmtNum(payoutOf(p))}</strong></td>
                                                <td className="is-center"><span className="c2-chip">{p.count}</span></td>
                                                <td className="c2-muted">{fmtDate(p.lastEntry)}</td>
                                                <td className="is-num">
                                                    <button
                                                        type="button"
                                                        className="c2-btn c2-btn--ghost c2-btn--sm"
                                                        onClick={() => setExpanded(prev => ({ ...prev, [p._id]: !isOpen }))}
                                                        aria-expanded={isOpen}
                                                    >
                                                        {isOpen ? t('pepitesAdmin.hide', 'Masquer') : t('pepitesAdmin.details', 'Détails')}
                                                        <Icon name={isOpen ? 'chevronUp' : 'chevronDown'} size={16} />
                                                    </button>
                                                </td>
                                            </tr>

                                            {/* ── Entrées du joueur ── */}
                                            {isOpen && (
                                                <tr className="pepites-detail">
                                                    <td colSpan={7}>
                                                        <table className="c2-table pepites-subtable">
                                                            <thead>
                                                                <tr>
                                                                    <th>{t('pepitesAdmin.date', 'Date')}</th>
                                                                    <th className="is-num">{t('pepitesAdmin.amount', 'Montant')}</th>
                                                                    <th>{t('pepitesAdmin.note', 'Note')}</th>
                                                                    <th></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {playerEntries.length === 0 ? (
                                                                    <tr><td colSpan={4} className="is-center c2-muted">{t('pepitesAdmin.noEntry', 'Aucune entrée')}</td></tr>
                                                                ) : playerEntries.map(e => (
                                                                    <tr key={e._id}>
                                                                        <td className="c2-muted">{fmtDate(e.createdAt)}</td>
                                                                        <td className="is-num">
                                                                            {editingId === e._id ? (
                                                                                <span className="pepites-edit">
                                                                                    <input
                                                                                        className="c2-input c2-input--sm pepites-edit__input"
                                                                                        type="number"
                                                                                        min="1"
                                                                                        aria-label={t('pepitesAdmin.newAmount', 'Nouveau montant')}
                                                                                        value={editAmount}
                                                                                        onChange={ev => setEditAmount(ev.target.value)}
                                                                                    />
                                                                                    <button type="button" className="c2-icon-btn c2-icon-btn--sm" onClick={() => saveEdit(e._id)} aria-label={t('pepitesAdmin.validate', 'Valider')}>
                                                                                        <Icon name="check" size={16} />
                                                                                    </button>
                                                                                    <button type="button" className="c2-icon-btn c2-icon-btn--sm" onClick={() => setEditingId(null)} aria-label={t('common.cancel', 'Annuler')}>
                                                                                        <Icon name="close" size={16} />
                                                                                    </button>
                                                                                </span>
                                                                            ) : (
                                                                                <strong className="pepites-gold">{fmtNum(e.amount)}</strong>
                                                                            )}
                                                                        </td>
                                                                        <td className="c2-muted pepites-note">{e.note || '—'}</td>
                                                                        <td className="is-num pepites-actions">
                                                                            {editingId !== e._id && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="c2-icon-btn c2-icon-btn--sm"
                                                                                    onClick={() => { setEditingId(e._id); setEditAmount(String(e.amount)); }}
                                                                                    aria-label={t('pepitesAdmin.editAmount', 'Modifier le montant')}
                                                                                    title={t('common.edit', 'Modifier')}
                                                                                >
                                                                                    <Icon name="pencil" size={16} />
                                                                                </button>
                                                                            )}
                                                                            <button
                                                                                type="button"
                                                                                className="c2-icon-btn c2-icon-btn--sm c2-icon-btn--danger"
                                                                                onClick={() => deleteEntry(e._id)}
                                                                                aria-label={t('pepitesAdmin.deleteEntry', 'Supprimer l\'entrée')}
                                                                                title={t('common.delete', 'Supprimer')}
                                                                            >
                                                                                <Icon name="trash" size={16} />
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* ── Historique des distributions ── */}
            <section className="c2-card c2-section">
                <h2 className="c2-section__title"><Icon name="history" size={20} />{t('pepitesAdmin.history', 'Historique des distributions')}</h2>
                {archives.length === 0 ? (
                    <p className="c2-muted">{t('pepitesAdmin.noHistory', 'Aucune distribution archivée pour l\'instant.')}</p>
                ) : (
                    <div className="pepites-history">
                        {archives.map((a) => {
                            const topPlayers = (a.summary || []).slice(0, 5);
                            return (
                                <article key={a._id} className="c2-tile pepites-archive">
                                    <div className="pepites-archive__head">
                                        <strong>{t('pepitesAdmin.distributionOf', 'Distribution du {date}', { date: fmtDate(a.distributedAt || a.createdAt) })}</strong>
                                        <span className="c2-muted">{t('pepitesAdmin.by', 'Par {name}', { name: a.distributedBy || t('common.unknown', 'Inconnu') })}</span>
                                    </div>
                                    <div className="pepites-archive__kpis">
                                        <span><strong className="pepites-gold">{fmtNum(a.totalPepites)}</strong> {t('pepitesAdmin.recycledLower', 'pépites recyclées')}</span>
                                        {a.totalPayout != null && (
                                            <span><strong className="pepites-payout">{fmtNum(a.totalPayout)}</strong> {t('pepitesAdmin.paidOut', 'reversées ({rate} %)', { rate: Math.round((a.payoutRate || 0) * 100) })}</span>
                                        )}
                                        <span><strong>{a.playerCount || 0}</strong> {t('pepitesAdmin.playersRewarded', 'joueurs récompensés')}</span>
                                        <span><strong>{a.totalEntries || 0}</strong> {t('pepitesAdmin.entriesCounted', 'entrées prises en compte')}</span>
                                    </div>
                                    {topPlayers.length > 0 && (
                                        <p className="pepites-archive__top">
                                            {t('pepitesAdmin.topPlayers', 'Top joueurs :')} {topPlayers.map((p) => `${p.playerName} (${fmtNum(p.payout ?? p.total)})`).join(' • ')}
                                        </p>
                                    )}
                                </article>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* ── Mode paiement ── */}
            {payMode && (
                <div className="c2-modal-backdrop" role="presentation">
                    <div className="c2-card c2-modal pepites-pay" role="dialog" aria-modal="true" aria-labelledby="pepites-pay-title">
                        <div className="c2-modal__head">
                            <h2 id="pepites-pay-title">{t('pepitesAdmin.payMode', 'Mode paiement')}</h2>
                            <button type="button" className="c2-icon-btn" onClick={() => setPayMode(false)} disabled={archiving} aria-label={t('common.close', 'Fermer')}>
                                <Icon name="close" size={20} />
                            </button>
                        </div>
                        <p className="c2-muted pepites-pay__intro">
                            {t('pepitesAdmin.payIntro', 'Donne en jeu le montant indiqué à chaque joueur ({rate} de ses pépites recyclées), puis coche-le. Les coches servent seulement à suivre le paiement.', { rate: ratePct })}
                        </p>

                        <div className="pepites-pay__totals">
                            <div className="c2-tile">
                                <span className="c2-muted">{t('pepitesAdmin.recycled', 'Pépites recyclées')}</span>
                                <strong>{fmtNum(grandTotal)}</strong>
                            </div>
                            <div className="c2-tile pepites-pay__main">
                                <span className="c2-muted">{t('pepitesAdmin.toGiveInGame', 'À donner en jeu ({rate})', { rate: ratePct })}</span>
                                <strong>{fmtNum(totalPayout)}</strong>
                            </div>
                            <div className="c2-tile">
                                <span className="c2-muted">{t('pepitesAdmin.paid', 'Payés')}</span>
                                <strong>{paidIds.size}/{summary.length} · {fmtNum(paidAmount)}</strong>
                            </div>
                        </div>

                        <ul className="pepites-pay__list">
                            {summary.map(p => {
                                const isPaid = paidIds.has(p._id);
                                return (
                                    <li key={p._id} className={`pepites-pay__row${isPaid ? ' is-paid' : ''}`}>
                                        <label className="pepites-pay__player">
                                            <input type="checkbox" className="c2-check" checked={isPaid} onChange={() => togglePaid(p._id)} />
                                            <span>
                                                <strong>{p.playerName}</strong>
                                                <span className="c2-muted">{t('pepitesAdmin.recycledCount', '{n} recyclées', { n: fmtNum(p.total) })}</span>
                                            </span>
                                        </label>
                                        <button type="button" className="pepites-pay__amount" onClick={() => copyAmount(payoutOf(p))} title={t('pepitesAdmin.copyAmount', 'Copier le montant')}>
                                            {fmtNum(payoutOf(p))}
                                            <Icon name="copy" size={14} />
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>

                        <div className="c2-modal__actions">
                            <button type="button" className="c2-btn c2-btn--ghost" onClick={() => setPayMode(false)} disabled={archiving}>{t('pepitesAdmin.later', 'Plus tard')}</button>
                            <button type="button" className="c2-btn c2-btn--primary" onClick={finishPayment} disabled={archiving}>
                                <Icon name="archive" size={18} />
                                {archiving ? t('pepitesAdmin.archiving', 'Archivage...') : t('pepitesAdmin.finishReset', 'Terminer et remettre à zéro')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PepitesPage;
